import { useQuery, useMutation, useQueryClient } from 'react-query';
import { proxyApi } from '../api/proxy';
import { ProxyConfigUpdate } from '../types/proxy';

export const useProxyConfig = () => {
  const queryClient = useQueryClient();

  const {
    data: config,
    isLoading,
    error,
    refetch,
  } = useQuery('proxyConfig', proxyApi.getConfig, {
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const updateConfigMutation = useMutation(
    (configData: ProxyConfigUpdate) => proxyApi.updateConfig(config!.id, configData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('proxyConfig');
        queryClient.invalidateQueries('proxyStatus');
      },
    }
  );

  const testConfigMutation = useMutation(proxyApi.testConfig);

  const healthCheckMutation = useMutation(proxyApi.healthCheck);

  const rotateProxiesMutation = useMutation(proxyApi.rotateProxies);

  const reloadConfigMutation = useMutation(proxyApi.reloadConfig, {
    onSuccess: () => {
      queryClient.invalidateQueries('proxyConfig');
      queryClient.invalidateQueries('proxyStatus');
    },
  });

  return {
    config,
    isLoading,
    error,
    refetch,
    updateConfig: updateConfigMutation.mutate,
    updateConfigAsync: updateConfigMutation.mutateAsync,
    isUpdating: updateConfigMutation.isLoading,
    testConfig: testConfigMutation.mutate,
    testConfigAsync: testConfigMutation.mutateAsync,
    isTesting: testConfigMutation.isLoading,
    testResults: testConfigMutation.data,
    healthCheck: healthCheckMutation.mutate,
    healthCheckAsync: healthCheckMutation.mutateAsync,
    isHealthChecking: healthCheckMutation.isLoading,
    rotateProxies: rotateProxiesMutation.mutate,
    rotateProxiesAsync: rotateProxiesMutation.mutateAsync,
    isRotating: rotateProxiesMutation.isLoading,
    reloadConfig: reloadConfigMutation.mutate,
    reloadConfigAsync: reloadConfigMutation.mutateAsync,
    isReloading: reloadConfigMutation.isLoading,
  };
};

export const useProxyStatus = () => {
  const {
    data: status,
    isLoading,
    error,
    refetch,
  } = useQuery('proxyStatus', proxyApi.getStatus, {
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  return {
    status,
    isLoading,
    error,
    refetch,
  };
};
